import { useEffect, useState } from 'react';
import { financialYearsApi } from '../../lib/api';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Can } from '../../lib/permissions';
import { useUiStore } from '../../stores/ui';
import type { FinancialYear } from '../../lib/types';

export function FinancialYearsPage() {
  const [years, setYears] = useState<FinancialYear[]>([]);
  const [error, setError] = useState<unknown>(null);
  const pushToast = useUiStore((s) => s.pushToast);

  function load() {
    financialYearsApi.list().then(({ items }) => setYears(items)).catch(setError);
  }

  useEffect(load, []);

  async function handleClose(fy: FinancialYear) {
    try {
      await financialYearsApi.close(fy.id, fy.revision);
      pushToast({ kind: 'success', message: `Financial year ${fy.label} closed.` });
      load();
    } catch (err) {
      setError(err);
    }
  }

  const columns: DataTableColumn<FinancialYear>[] = [
    { key: 'range', header: 'Period', render: (f) => `${f.label} (${f.startsOn} → ${f.endsOn})` },
    { key: 'status', header: 'Status', render: (f) => (f.isClosed ? <span className="text-[var(--text-faint)]">Closed</span> : <span className="text-[var(--success)]">Open</span>) },
    {
      key: 'actions',
      header: '',
      render: (f) =>
        !f.isClosed ? (
          <Can permission="company:financial_year:close">
            <button onClick={() => handleClose(f)} className="rounded-[3px] border border-[var(--border)] px-1.5 py-0.5 text-[11px] hover:bg-[var(--bg-hover)]">
              Close
            </button>
          </Can>
        ) : null,
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-3 border-b border-[var(--border)] pb-2">
        <h1 className="text-[14px] font-semibold">Financial years</h1>
      </div>
      {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}
      <DataTable columns={columns} rows={years} getRowKey={(f) => f.id} emptyMessage="No financial years yet." />
    </div>
  );
}
